import React, { useEffect, useRef, useState } from "react";
import { AdvancedMarker, Map as GMap, Pin } from "@vis.gl/react-google-maps";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";

function generateCircleCoordinates(coordinates, radiusKm = 50, numPoints = 36) {
  // Earth's radius in kilometers
  const EARTH_RADIUS = 6371;

  // Function to convert degrees to radians
  const toRadians = (degrees) => degrees * (Math.PI / 180);

  // Function to convert radians to degrees
  const toDegrees = (radians) => radians * (180 / Math.PI);

  // Validate input
  if (!coordinates || coordinates.length === 0) {
    throw new Error("Coordinates array is empty");
  }

  // Calculate the center point (average of all input coordinates)
  const centerLat =
    coordinates.reduce((sum, coord) => sum + coord.lat, 0) / coordinates.length;
  const centerLon =
    coordinates.reduce((sum, coord) => sum + coord.lon, 0) / coordinates.length;

  // Calculate the maximum distance between input coordinates
  let maxDistance = 0;
  for (let i = 0; i < coordinates.length; i++) {
    for (let j = i + 1; j < coordinates.length; j++) {
      const lat1 = coordinates[i].lat;
      const lon1 = coordinates[i].lon;
      const lat2 = coordinates[j].lat;
      const lon2 = coordinates[j].lon;

      const dLat = toRadians(lat2 - lat1);
      const dLon = toRadians(lon2 - lon1);

      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRadians(lat1)) *
          Math.cos(toRadians(lat2)) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);

      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = EARTH_RADIUS * c;

      maxDistance = Math.max(maxDistance, distance);
    }
  }

  // Calculate zoom level (1-17 scale)
  // This is a rough approximation and may need fine-tuning
  const calculateZoom = (maxDistanceKm) => {
    const zoomLevels = [
      { maxDist: 20000, zoom: 3 }, // World
      { maxDist: 10000, zoom: 4 }, // Continent
      { maxDist: 5000, zoom: 5 }, // Large country
      { maxDist: 2000, zoom: 6 }, // Country
      { maxDist: 1000, zoom: 7 }, // Large state/region
      { maxDist: 500, zoom: 8 }, // State/region
      { maxDist: 200, zoom: 9 }, // Metropolitan area
      { maxDist: 100, zoom: 10 }, // City
      { maxDist: 50, zoom: 11 }, // Large city
      { maxDist: 20, zoom: 12 }, // City district
      { maxDist: 10, zoom: 13 }, // Neighborhood
      { maxDist: 5, zoom: 14 }, // Large neighborhood
      { maxDist: 2, zoom: 15 }, // Small neighborhood
      { maxDist: 1, zoom: 16 }, // Street
      { maxDist: 0.5, zoom: 17 }, // Block
    ];

    for (let level of zoomLevels) {
      if (maxDistanceKm > level.maxDist) {
        return level.zoom;
      }
    }
    return 17; // Maximum zoom if distances are very small
  };

  // Generate circle coordinates
  const circleCoordinates = [];

  for (let i = 0; i < numPoints; i++) {
    const angle = (i * 360) / numPoints;
    const angleRad = toRadians(angle);

    // Calculate new point using the Haversine formula
    const latRad = toRadians(centerLat);
    const lonRad = toRadians(centerLon);

    const angularDistance = radiusKm / EARTH_RADIUS;

    const newLatRad = Math.asin(
      Math.sin(latRad) * Math.cos(angularDistance) +
        Math.cos(latRad) * Math.sin(angularDistance) * Math.cos(angleRad)
    );

    const newLonRad =
      lonRad +
      Math.atan2(
        Math.sin(angleRad) * Math.sin(angularDistance) * Math.cos(latRad),
        Math.cos(angularDistance) - Math.sin(latRad) * Math.sin(newLatRad)
      );

    const newLat = toDegrees(newLatRad);
    const newLon = toDegrees(newLonRad);

    circleCoordinates.push({
      lat: newLat,
      lon: newLon,
    });
  }

  // Calculate zoom level
  const zoomLevel = calculateZoom(maxDistance);

  return {
    center: {
      lat: centerLat,
      lon: centerLon,
    },
    coordinates: circleCoordinates,
    zoom: zoomLevel,
    maxDistance: maxDistance,
  };
}

const Map = (props) => {
  const { locations, nameList } = props;
  const [centerP, setTheCenter] = useState({});
  const [zoomScl, setZoom] = useState(15);
  const [mrf, setMrf] = useState();
  const modifiedPegmanRefs = useRef(new Set()); // Track which pegman elements we've modified

  useEffect(() => {
    const { center, zoom } = generateCircleCoordinates(
      locations.map((l) => {
        return { lat: parseFloat(l.lat), lon: parseFloat(l.lon) };
      })
    );

    setTheCenter(center);
    setZoom(zoom/1.4);
  }, []);

  useEffect(() => {
    // Create MutationObserver to watch for new pegman elements
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          const pegmanElements = document.querySelectorAll(".gm-svpc");
          pegmanElements.forEach((pegman) => {
            // Only modify title if we haven't already
            if (!modifiedPegmanRefs.current.has(pegman)) {
              pegman.title = "Drag Lex";
              modifiedPegmanRefs.current.add(pegman);
            }
          });
        }
      });
    });

    // Start observing the document with the configured parameters
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Initial check for existing pegman elements
    const existingPegman = document.querySelectorAll(".gm-svpc");
    existingPegman.forEach((pegman) => {
      if (!modifiedPegmanRefs.current.has(pegman)) {
        pegman.title = "Drag Lex";
        modifiedPegmanRefs.current.add(pegman);
      }
    });

    // Cleanup observer on component unmount
    return () => {
      observer.disconnect();
      modifiedPegmanRefs.current.clear();
    };
  }, []);

  return (
    <div
      style={{
        width: "92%",
        margin: "0 auto",
        borderRadius: "10px",
        marginTop: "20px",
        marginBottom: "50px",
        height: "290px",
        overflow: "hidden",
      }}
    >
      {Object.keys(centerP).length === 2 && (
        <GMap
          defaultZoom={zoomScl}
          defaultCenter={{
            lat: parseFloat(centerP.lat),
            lng: parseFloat(centerP.lon),
          }}
          mapId={"beatai"}
        >
          {locations.map((poi, i) => {
            if (!parseFloat(poi.lat) || !parseFloat(poi.lon)) return null;

            return (
              <AdvancedMarker
                ref={r => setMrf(r)}
                onClick={() => {
                  if (props.navigateEvent) {
                    props.navigateEvent(i);
                  }
                }}
                title={`${
                  Array.isArray(nameList) && nameList.length > 0
                    ? nameList[i]
                    : ""
                }`}
                className="marker-map"
                key={i}
                position={{
                  lat: parseFloat(poi.lat),
                  lng: parseFloat(poi.lon),
                }}
              >
                <Pin
                  background={"#9c66ff"}
                  glyphColor={"#472cde"}
                  borderColor={"#472cde"}
                />
              </AdvancedMarker>
            );
          })}
        </GMap>
      )}
    </div>
  );
};

export default Map;